import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Intercepts the browser Back button while inside an active install flow.
 * Uses the sentinel pattern: pushes a dummy history entry on mount so the
 * first Back press fires popstate rather than leaving the page.
 *
 * @param {boolean} isActive – enable the guard (pass false to skip)
 * @returns {{ dialogOpen: boolean, onLeave: Function, onStay: Function }}
 */
export function useBackGuard(isActive) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    // Push a sentinel entry so the first Back lands here instead of the previous page
    window.history.pushState(null, "", window.location.href);

    function handlePopState() {
      // Re-arm immediately before opening the dialog
      window.history.pushState(null, "", window.location.href);
      setDialogOpen(true);
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isActive]);

  const onLeave = () => {
    setDialogOpen(false);
    navigate("/installer/");
  };

  const onStay = () => {
    setDialogOpen(false);
    // Guard is already re-armed in the popstate handler
  };

  return { dialogOpen, onLeave, onStay, openDialog: () => setDialogOpen(true) };
}
